import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronRight,
  Layers3,
  RefreshCw,
  X,
} from "lucide-react";
import { useDashData } from "../../contexts/DashDataContext";
import { useAgentTasks } from "../../hooks/useAgentTasks";
import type { ActionPhase } from "../../hooks/useActionTracker";
import {
  parseOrcaGuidanceV1,
  type OrcaGuidanceAttentionItem,
} from "../../types/orcaGuidance";
import { isReportGenerationActive } from "../../utils/reportDataComposition";
import {
  normalizeCapabilityNavigationTarget,
  type CapabilityNavigationHandler,
  type CapabilityNavigationTarget,
} from "../../config/capabilityNavigation";
import { ShellActivityControl } from "./ShellSurfaceControls";

type ActivityCenterTone = "new" | "running" | "attention" | "complete" | "quiet";

export interface ActivityCenterItem {
  id: string;
  title: string;
  meta?: string;
  target?: CapabilityNavigationTarget;
  kind: "notification" | "activity" | "approval" | "agent" | "recent";
}

export interface ActivityCenterSection {
  id: "notifications" | "activity" | "approvals" | "agents" | "recent";
  label: string;
  summary?: string;
  tone: ActivityCenterTone;
  items: ActivityCenterItem[];
}

interface ActivityCenterModelInput {
  project?: any;
  propertyFacts?: any;
  reportData?: any;
  activities?: any[];
  agentTasks?: any[];
  actionPhases?: ActionPhase[];
  currentPhase?: ActionPhase | null;
}

interface ActivityCenterPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenCapability: CapabilityNavigationHandler;
  onAttentionChange?: (hasAttention: boolean) => void;
  triggerVariant?: "shell" | "quick-access";
  portalTarget?: HTMLElement | null;
  triggerTabIndex?: number;
  hideTrigger?: boolean;
}

const RUNNING_STATUSES = new Set(["approved", "queued", "running", "in_progress"]);
const HYDRATION_STATUSES = new Set([
  "queued",
  "running",
  "in_progress",
  "quality_retry_pending",
  "quality_retrying",
]);
const APPROVAL_STATUSES = new Set(["awaiting_approval", "checkpoint_waiting"]);
const COMPLETE_STATUSES = new Set(["complete", "completed", "simulated"]);
const TERMINAL_AGENT_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "expired",
  "rejected",
]);
const ACTIONABLE_GUIDANCE_PRIORITIES = new Set(["urgent", "action"]);
const MAX_NOTIFICATION_ITEMS = 12;
const QUICK_ACTIVITY_POPOVER_WIDTH = 260;

function normalizedStatus(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function activityTitle(activity: any, fallback = "Project activity"): string {
  for (const value of [activity?.title, activity?.label, activity?.message, activity?.description, activity?.action]) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return fallback;
}

function activityTimestamp(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === "object" && "toMillis" in value && typeof value.toMillis === "function") {
    return value.toMillis();
  }
  if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate().getTime();
  }
  return 0;
}

function formatRelativeTime(value: unknown): string | undefined {
  const timestamp = activityTimestamp(value);
  if (!timestamp) return undefined;
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return "Now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function activityIdentifier(activity: any, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const direct = activity?.[key];
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    const metadata = activity?.metadata?.[key];
    if (typeof metadata === "string" && metadata.trim()) return metadata.trim();
  }
  return undefined;
}

export function activityNavigationTarget(activity: any): CapabilityNavigationTarget | undefined {
  const options = { allowSystem: true } as const;
  const directTarget = normalizeCapabilityNavigationTarget(
    activity?.target
      ?? activity?.metadata?.target
      ?? { capabilityId: activityIdentifier(activity, "capabilityId") },
    options,
  );
  const rawTabId = activityIdentifier(activity, "tabId");
  const tabId = rawTabId?.startsWith("cap-") ? rawTabId.slice(4) : rawTabId;
  const tabTarget = normalizeCapabilityNavigationTarget(tabId, options);
  const type = `${activity?.type || ""} ${activity?.action || ""}`.toLowerCase();
  const inferredId = type.includes("report")
    ? "adu_project_report"
    : type.includes("fact") || type.includes("hydrat")
      ? "project_facts"
      : type.includes("phase") || type.includes("milestone")
        ? "adu_tasks"
        : type.includes("note")
          ? "user_notes"
          : type.includes("artifact")
            ? "project_artifacts"
            : type.includes("agent") || type.includes("approval")
              ? "agent_activity"
              : undefined;
  const target = directTarget
    ?? tabTarget
    ?? normalizeCapabilityNavigationTarget(inferredId, options);
  if (!target || target.entity) return target ?? undefined;

  const entity = target.capabilityId === "adu_tasks"
    ? (() => {
        const id = activityIdentifier(activity, "milestoneId", "phaseId");
        if (!id) return undefined;
        const phaseKey = activityIdentifier(activity, "phaseKey");
        return phaseKey
          ? { kind: "milestone" as const, id, phaseKey }
          : { kind: "milestone" as const, id };
      })()
    : target.capabilityId === "user_notes"
      ? (() => {
          const id = activityIdentifier(activity, "noteId");
          return id ? { kind: "note" as const, id } : undefined;
        })()
      : ["project_artifacts", "project_generated_items", "workspace_artifact"].includes(target.capabilityId)
        ? (() => {
            const id = activityIdentifier(
              activity,
              "artifactId",
              "widgetId",
              "sourceDraftArtifactId",
            );
            return id ? { kind: "artifact" as const, id } : undefined;
          })()
        : undefined;

  return entity
    ? normalizeCapabilityNavigationTarget({ ...target, entity }, options) ?? target
    : target;
}

function countSummary(count: number, singular: string, plural = `${singular}s`): string | undefined {
  if (count === 0) return undefined;
  return `${count} ${count === 1 ? singular : plural}`;
}

function exactEntityTargetKey(target: CapabilityNavigationTarget | undefined): string | null {
  if (!target?.entity) return null;
  return `${target.capabilityId}:${target.entity.kind}:${target.entity.id}`;
}

function deduplicateItems(items: ActivityCenterItem[]): ActivityCenterItem[] {
  const seenIds = new Set<string>();
  const seenTargets = new Set<string>();
  return items.filter((item) => {
    const targetKey = exactEntityTargetKey(item.target);
    if (seenIds.has(item.id) || (targetKey && seenTargets.has(targetKey))) return false;
    seenIds.add(item.id);
    if (targetKey) seenTargets.add(targetKey);
    return true;
  });
}

function projectedGuidanceItems(project: any): OrcaGuidanceAttentionItem[] {
  const guidance = parseOrcaGuidanceV1(project?.directive);
  if (!guidance || guidance.scope !== "project") return [];

  // A server-projected list is authoritative, including an intentionally
  // empty list. Older projects fall back to their one current Directive.
  if (guidance.attentionItems !== undefined) return guidance.attentionItems;
  if (!ACTIONABLE_GUIDANCE_PRIORITIES.has(guidance.priority)) return [];
  return [{
    id: `directive:${guidance.sourceRevision || guidance.kind}`,
    kind: guidance.kind,
    text: guidance.text,
    priority: guidance.priority,
    actionLabel: "Review next step",
    ...(guidance.target ? { target: guidance.target } : {}),
  }];
}

function hasAuthoritativeAttentionItems(project: any): boolean {
  const guidance = parseOrcaGuidanceV1(project?.directive);
  return guidance?.scope === "project" && Array.isArray(project?.directive?.attentionItems);
}

function currentMilestoneItems(
  currentPhase: ActionPhase | null,
  actionPhases: ActionPhase[],
): ActivityCenterItem[] {
  const milestone = currentPhase && currentPhase.status !== "complete"
    ? currentPhase
    : [...actionPhases]
      .filter((candidate) => candidate.status !== "complete")
      .sort((left, right) => left.order - right.order)[0];
  return milestone
    ? [{
      id: `milestone:${milestone.id}`,
      title: milestone.title,
      meta: "Milestone",
      target: {
        capabilityId: "adu_tasks",
        entity: {
          kind: "milestone",
          id: milestone.id,
          ...(milestone.phaseKey ? { phaseKey: milestone.phaseKey } : {}),
        },
      },
      kind: "notification",
    }]
    : [];
}

export function buildActivityCenterModel({
  project = null,
  propertyFacts = null,
  reportData = null,
  activities = [],
  agentTasks = [],
  actionPhases = [],
  currentPhase = null,
}: ActivityCenterModelInput): ActivityCenterSection[] {
  const guidanceItems = projectedGuidanceItems(project);
  const usesAuthoritativeAttentionItems = hasAuthoritativeAttentionItems(project);
  const milestoneNotifications = currentMilestoneItems(currentPhase, actionPhases);
  const milestoneNotificationsByTarget = new Map(
    milestoneNotifications.map((item) => [exactEntityTargetKey(item.target), item]),
  );
  const milestoneIds = new Set(actionPhases.map((milestone) => milestone.id));
  const guidanceNotifications = guidanceItems
    .filter((item) => item.kind !== "approval")
    .flatMap((item): ActivityCenterItem[] => {
      const targetKey = exactEntityTargetKey(item.target);
      const linkedMilestone = targetKey
        ? milestoneNotificationsByTarget.get(targetKey)
        : undefined;
      if (linkedMilestone) return [linkedMilestone];
      if (item.target?.entity?.kind === "milestone" && milestoneIds.has(item.target.entity.id)) {
        return [];
      }
      return [{
        id: item.id,
        title: item.text,
        target: item.target,
        kind: "notification",
      }];
    });
  const activityNotifications = activities
    .filter((activity) => activity?.unread === true || activity?.read === false)
    .map((activity, index): ActivityCenterItem => ({
      id: activity?.id || `notification-${index}`,
      title: activityTitle(activity, "New project update"),
      meta: formatRelativeTime(activity?.timestamp || activity?.createdAt),
      target: activityNavigationTarget(activity),
      kind: "notification",
    }));
  const notifications = deduplicateItems([
    ...guidanceNotifications,
    ...(usesAuthoritativeAttentionItems ? [] : milestoneNotifications),
    ...activityNotifications,
  ]).slice(0, MAX_NOTIFICATION_ITEMS);

  const running: ActivityCenterItem[] = [];
  const hydrationStatus = normalizedStatus(propertyFacts?.lifecycleState || propertyFacts?.status);
  if (HYDRATION_STATUSES.has(hydrationStatus)) {
    running.push({
      id: "project-facts-hydration",
      title: "Updating Project Facts",
      meta: propertyFacts?.domainsCompleted && propertyFacts?.domainsTotal
        ? `${propertyFacts.domainsCompleted} of ${propertyFacts.domainsTotal}`
        : "Running",
      target: { capabilityId: "project_facts" },
      kind: "activity",
    });
  }

  if (
    RUNNING_STATUSES.has(normalizedStatus(project?.reportUpdateStatus))
    || isReportGenerationActive(reportData)
  ) {
    running.push({
      id: "report-generation",
      title: "Updating ADU Report",
      meta: normalizedStatus(project?.reportUpdateStatus) === "queued" ? "Queued" : "Running",
      target: { capabilityId: "adu_project_report" },
      kind: "activity",
    });
  }

  activities
    .filter((activity) => RUNNING_STATUSES.has(normalizedStatus(activity?.status)))
    .forEach((activity, index) => running.push({
      id: activity?.id || `activity-${index}`,
      title: activityTitle(activity),
      meta: formatRelativeTime(activity?.timestamp || activity?.updatedAt) || "Running",
      target: activityNavigationTarget(activity),
      kind: "activity",
    }));

  const guidanceApprovals = guidanceItems
    .filter((item) => item.kind === "approval")
    .map((item): ActivityCenterItem => ({
      id: item.id,
      title: item.text,
      meta: item.actionLabel,
      target: item.target,
      kind: "approval",
    }));
  const agentApprovals = agentTasks
    .filter((task) => task?.lifecycle?.needsApproval === true || APPROVAL_STATUSES.has(normalizedStatus(task?.lifecycle?.state || task?.status)))
    .map((task, index): ActivityCenterItem => ({
      id: task?.id || `approval-${index}`,
      title: activityTitle(task, "Agent approval needed"),
      meta: "Now",
      target: { capabilityId: "agent_activity" },
      kind: "approval",
    }));
  const approvals = deduplicateItems([...guidanceApprovals, ...agentApprovals]);

  const agents = agentTasks
    .filter((task) => {
      const status = normalizedStatus(task?.lifecycle?.state || task?.status);
      return task?.lifecycle?.needsApproval !== true
        && !APPROVAL_STATUSES.has(status)
        && !TERMINAL_AGENT_STATUSES.has(status);
    })
    .map((task, index): ActivityCenterItem => ({
      id: task?.id || `agent-${index}`,
      title: activityTitle(task, "Agent work"),
      meta: APPROVAL_STATUSES.has(normalizedStatus(task?.lifecycle?.state || task?.status))
        ? "Awaiting approval"
        : "Active",
      target: { capabilityId: "agent_activity" },
      kind: "agent",
    }));

  agentTasks
    .filter((task) => RUNNING_STATUSES.has(normalizedStatus(task?.lifecycle?.state || task?.status)))
    .forEach((task, index) => running.push({
      id: `agent-running-${task?.id || index}`,
      title: activityTitle(task, "Agent work"),
      meta: "Running",
      target: { capabilityId: "agent_activity" },
      kind: "activity",
    }));

  const recent = [
    ...activities
      .filter((activity) => COMPLETE_STATUSES.has(normalizedStatus(activity?.status)))
      .map((activity, index): ActivityCenterItem & { sortAt: number } => ({
        id: activity?.id || `recent-${index}`,
        title: activityTitle(activity),
        meta: formatRelativeTime(activity?.timestamp || activity?.updatedAt),
        target: activityNavigationTarget(activity),
        kind: "recent",
        sortAt: activityTimestamp(activity?.timestamp || activity?.updatedAt),
      })),
    ...agentTasks
      .filter((task) => COMPLETE_STATUSES.has(normalizedStatus(task?.lifecycle?.state || task?.status)))
      .map((task, index): ActivityCenterItem & { sortAt: number } => ({
        id: task?.id || `recent-agent-${index}`,
        title: activityTitle(task, "Agent work completed"),
        meta: formatRelativeTime(task?.updatedAt || task?.createdAt),
        target: { capabilityId: "agent_activity" },
        kind: "recent",
        sortAt: activityTimestamp(task?.updatedAt || task?.createdAt),
      })),
  ]
    .sort((left, right) => right.sortAt - left.sortAt)
    .slice(0, 3)
    .map(({ sortAt: _sortAt, ...item }) => item);

  return [
    {
      id: "notifications",
      label: "Notifications",
      summary: countSummary(notifications.length, "item"),
      tone: notifications.length ? "new" : "quiet",
      items: notifications,
    },
    {
      id: "activity",
      label: "Activity",
      summary: countSummary(running.length, "running", "running"),
      tone: running.length ? "running" : "quiet",
      items: running,
    },
    {
      id: "approvals",
      label: "Approvals",
      summary: countSummary(approvals.length, "needed", "needed"),
      tone: approvals.length ? "attention" : "quiet",
      items: approvals,
    },
    {
      id: "agents",
      label: "Agents",
      summary: countSummary(agents.length, "active", "active"),
      tone: agents.length ? "running" : "quiet",
      items: agents,
    },
    {
      id: "recent",
      label: "Recent",
      summary: countSummary(recent.length, "complete", "complete"),
      tone: recent.length ? "complete" : "quiet",
      items: recent,
    },
  ];
}

function SectionStatus({ section }: { section: ActivityCenterSection }) {
  if (!section.summary) return null;
  return (
    <span className={`bearing-activity-status is-${section.tone}`} role="status">
      {section.tone === "running" && <RefreshCw size={12} aria-hidden="true" />}
      {(section.tone === "new" || section.tone === "attention") && (
        <span className="bearing-activity-status-mark" aria-hidden="true" />
      )}
      {section.summary}
    </span>
  );
}

function ItemIcon({ kind }: { kind: ActivityCenterItem["kind"] }) {
  if (kind === "approval") return <AlertTriangle aria-hidden="true" />;
  if (kind === "agent") return <Bot aria-hidden="true" />;
  if (kind === "recent") return <Check aria-hidden="true" />;
  return <RefreshCw aria-hidden="true" />;
}

export function ActivityCenterPopover({
  open,
  onOpenChange,
  onOpenCapability,
  onAttentionChange,
  triggerVariant = "shell",
  portalTarget = null,
  triggerTabIndex,
  hideTrigger = false,
}: ActivityCenterPopoverProps) {
  const { project, propertyFacts, reportData, activities, actionPhases, currentPhase } = useDashData();
  const { tasks: agentTasks } = useAgentTasks(project?.id);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [isPhoneSheet, setIsPhoneSheet] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [quickPosition, setQuickPosition] = useState<{
    left: number;
    bottom: number;
  } | null>(null);
  const model = useMemo(() => buildActivityCenterModel({
    project,
    propertyFacts,
    reportData,
    activities,
    agentTasks,
    actionPhases,
    currentPhase,
  }), [actionPhases, activities, agentTasks, currentPhase, project, propertyFacts, reportData]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const phoneQuery = window.matchMedia("(max-width: 640px)");
    const syncPhonePresentation = () => setIsPhoneSheet(phoneQuery.matches);
    syncPhonePresentation();
    phoneQuery.addEventListener("change", syncPhonePresentation);
    return () => phoneQuery.removeEventListener("change", syncPhonePresentation);
  }, []);

  useEffect(() => {
    if (!open) return;
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      openerRef.current = activeElement;
    }
  }, [open]);

  const requestClose = useCallback((restoreFocus = false) => {
    if (isClosing) return;
    const finishClose = () => {
      closeTimerRef.current = null;
      setIsClosing(false);
      onOpenChange(false);
      if (restoreFocus) (openerRef.current ?? triggerRef.current)?.focus();
    };
    if (!isPhoneSheet || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      finishClose();
      return;
    }
    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(finishClose, 220);
  }, [isClosing, isPhoneSheet, onOpenChange]);

  useEffect(() => () => {
    if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    setExpanded((current) => {
      const next = new Set(current);
      if (model.find((section) => section.id === "activity")?.items.length) next.add("activity");
      if (model.find((section) => section.id === "approvals")?.items.length) next.add("approvals");
      return next;
    });
  }, [model, open]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !anchorRef.current?.contains(target)
        && !popoverRef.current?.contains(target)
      ) {
        requestClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose(true);
        return;
      }
      if (event.key !== "Tab" || !isPhoneSheet || !popoverRef.current) return;
      const focusable = Array.from(popoverRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPhoneSheet, open, requestClose]);

  useEffect(() => {
    if (!open || !isPhoneSheet) return;
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.documentElement.style.overflow = previousOverflow;
    };
  }, [isPhoneSheet, open]);

  useLayoutEffect(() => {
    if (!open || triggerVariant !== "quick-access") {
      setQuickPosition(null);
      return;
    }

    const updatePosition = () => {
      if (window.matchMedia?.("(max-width: 640px)").matches) {
        setQuickPosition(null);
        return;
      }
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(QUICK_ACTIVITY_POPOVER_WIDTH, window.innerWidth - 24);
      const left = Math.max(
        12,
        Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 12),
      );
      setQuickPosition({
        left,
        bottom: window.innerHeight - rect.top + 8,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [open, triggerVariant]);

  const hasAttention = model.some((section) =>
    ["new", "running", "attention"].includes(section.tone) && section.items.length > 0,
  );

  useEffect(() => {
    onAttentionChange?.(hasAttention);
  }, [hasAttention, onAttentionChange]);

  const activityLayer = open && (
    <>
      <button
        type="button"
        className={`bearing-activity-center-backdrop${isClosing ? " is-closing" : ""}`}
        aria-label="Close Activity Center"
        tabIndex={-1}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => requestClose()}
      />
      <section
        ref={popoverRef}
        id="bearing-activity-center"
        className={`bearing-activity-center-popover bearing-menu-popover${triggerVariant === "quick-access" ? " is-quick-access" : ""}${isClosing ? " is-closing" : ""}`}
        role="dialog"
        aria-label="Activity Center"
        aria-modal={isPhoneSheet}
        aria-hidden={isClosing}
        inert={isClosing ? ("" as unknown as boolean) : undefined}
        style={quickPosition ? {
          left: quickPosition.left,
          bottom: quickPosition.bottom,
        } : undefined}
      >
        <header className="bearing-activity-center-header">
          <h2>Activity Center</h2>
          <button
            ref={closeRef}
            type="button"
            className="bearing-activity-center-close"
            aria-label="Close Activity Center"
            onClick={() => requestClose()}
          >
            <X size={20} strokeWidth={1.9} aria-hidden="true" />
          </button>
        </header>
        <div className="bearing-activity-center-content">
          {model.map((section) => {
        const isExpanded = expanded.has(section.id);
        const contentId = `bearing-activity-section-${section.id}`;
        return (
          <div
            className={`bearing-activity-section ${isExpanded ? "is-expanded" : ""}`}
            data-activity-section={section.id}
            key={section.id}
          >
            <button
              type="button"
              className="bearing-activity-section-trigger bearing-menu-popover-item"
              aria-expanded={isExpanded}
              aria-controls={contentId}
              onClick={() => setExpanded((current) => {
                const next = new Set(current);
                if (next.has(section.id)) next.delete(section.id);
                else next.add(section.id);
                return next;
              })}
            >
              <span className="bearing-activity-section-label">{section.label}</span>
              <SectionStatus section={section} />
              <ChevronRight className="bearing-activity-section-chevron" aria-hidden="true" />
            </button>
            {isExpanded && (
              <div className="bearing-activity-section-content" id={contentId}>
                {section.items.length === 0 ? (
                  <p className="bearing-activity-empty">
                    Nothing needs attention here.
                  </p>
                ) : section.items.map((item) => {
                  const content = (
                    <>
                      <span className="bearing-activity-item-icon"><ItemIcon kind={item.kind} /></span>
                      <span className="bearing-activity-item-title">{item.title}</span>
                      {item.meta && <span className="bearing-activity-item-meta">{item.meta}</span>}
                      {item.target && <ChevronRight className="bearing-activity-item-open" aria-hidden="true" />}
                    </>
                  );
                  return item.target ? (
                    <button
                      type="button"
                      className="bearing-activity-item"
                      key={item.id}
                      onClick={() => {
                        onOpenCapability(item.target!);
                        onOpenChange(false);
                      }}
                    >
                      {content}
                    </button>
                  ) : (
                    <div className="bearing-activity-item" key={item.id}>{content}</div>
                  );
                })}
              </div>
            )}
          </div>
        );
          })}
        </div>
      </section>
    </>
  );

  const resolvedPortalTarget = isPhoneSheet
    ? portalTarget?.closest<HTMLElement>(".orca-engine-4pane") ?? portalTarget
    : portalTarget;

  return (
    <div
      className={`bearing-activity-center-anchor${triggerVariant === "quick-access" ? " is-quick-access" : ""}`}
      ref={anchorRef}
    >
      {!hideTrigger && (triggerVariant === "quick-access" ? (
        <button
          ref={triggerRef}
          type="button"
          data-control="activity-center"
          className={`bearing-home-quick-tile${open ? " is-active" : ""}`}
          aria-label={`${open ? "Close" : "Open"} Activity Center`}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls="bearing-activity-center"
          tabIndex={triggerTabIndex}
          onClick={() => open ? requestClose() : onOpenChange(true)}
          title="Activity Center"
        >
          <Layers3 size={20} strokeWidth={1.8} aria-hidden="true" />
          {hasAttention && (
            <span className="bearing-shell-activity-dot" aria-hidden="true" />
          )}
          <span>Activity</span>
        </button>
      ) : (
        <ShellActivityControl
          ref={triggerRef}
          open={open}
          onToggle={() => open ? requestClose() : onOpenChange(true)}
          hasAttention={hasAttention}
          className="bearing-shell-activity-control"
        />
      ))}
      {activityLayer && (
        triggerVariant === "quick-access" && resolvedPortalTarget
          ? createPortal(activityLayer, resolvedPortalTarget)
          : activityLayer
      )}
    </div>
  );
}

export default ActivityCenterPopover;
